import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import deburr from 'lodash/deburr';
import cn from 'classnames';
import Downshift from 'downshift';
import { usePopper } from 'react-popper';
import { detectOverflow } from '@popperjs/core';
import Input from './Input';
import ListItem from './ListItem';
import './styles.scss';

// function to render suggestions inside the Downshift dropdown component
const renderSuggestions = (
  pendingValue,
  activeValue,
  options,
  { getItemProps, showEmpty = true, highlightedIndex } = {},
) => {
  const inputValue = deburr(pendingValue.trim()).toLowerCase();
  const inputLength = inputValue.length;

  // if empty shows all options or not
  return inputLength === 0 && !showEmpty
    ? []
    : options
        // filter out options that don't match the inputValue
        .filter((option) => option.label.toLowerCase().includes(inputValue))
        .map((item, index) => (
          <ListItem
            key={`${item.label}-${item.value}`}
            item={item}
            matchingValue={activeValue === item.label}
            highlightedIndex={highlightedIndex}
            index={index}
            getItemProps={getItemProps}
          />
        ));
};

const sameWidth = {
  name: 'sameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
  },
};

const maxSize = {
  name: 'maxSize',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['offset', 'preventOverflow', 'flip'],
  fn({ state, name, options }) {
    const overflow = detectOverflow(state, options);
    const { x, y } = state.modifiersData.preventOverflow || { x: 0, y: 0 };
    const { width, height } = state.rects.popper;
    const [basePlacement] = state.placement.split('-');

    const widthProp = basePlacement === 'left' ? 'left' : 'right';
    const heightProp = basePlacement === 'top' ? 'top' : 'bottom';

    state.modifiersData[name] = {
      width: width - overflow[widthProp] - x,
      height: height - overflow[heightProp] - y,
    };
  },
};

const applyMaxSize = {
  name: 'applyMaxSize',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['maxSize'],
  fn({ state }) {
    const { height } = state.modifiersData.maxSize;
    setTimeout(() => {
      // Clamp the maximum height to 384px
      const maxHeight = Math.min(height, 384);
      state.styles.popper = {
        ...state.styles.popper,
        maxHeight: `${maxHeight}px`,
      };
      // Apply `max-height` rule for the list parent too
      const listParent = state.elements.popper.querySelector('ul');
      if (listParent) {
        listParent.style.maxHeight = `${maxHeight}px`;
      }
    }, 10);
  },
};

const AutocompleteSelect = ({
  className,
  clearable,
  customReset,
  downshiftProps,
  disableDropdown,
  disableAutoFocus = false,
  openOnClick = false,
  icon,
  onChange,
  options = [],
  placeholder,
  showEmpty,
  value,
  inputProps,
  onInputChange,
  menuRef,
  stateChangeCallback = () => {},
  inputClassName,
  containerRef,
  useDefaultSelect = false,
  itemStringMaxLength = 0,
  portalContainerClassName = 'z-max',
  listClassName,
  trackingId,
  ...props
}) => {
  const [pendingInput, setPendingInput] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [optionAriaLabel, setOptionAriaLabel] = useState('');
  const downshiftRef = useRef();
  const inputRef = useRef();
  const listWrapperClassnameRef = useRef();
  const isTextMatched = useRef(false);
  const listItemsRef = useRef([]);
  const currentActiveIndex = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'bottom',
      modifiers: [sameWidth, maxSize, applyMaxSize],
    },
  );

  // Here is special case where we need to switch the position of the dropdown select when it exceeds its parent element  container.
  // The code below adds the height of the menuRef (dropdown) with its top position.
  // if it is greater than the parent container bottom position we then switch the dropdown style.
  if (
    useDefaultSelect &&
    containerRef &&
    containerRef.current &&
    menuRef &&
    menuRef.current
  ) {
    const containerButtomValue =
      containerRef.current.getBoundingClientRect().bottom;
    const menuTopValue = menuRef.current.getBoundingClientRect().top;
    const menuHeightValue = menuRef.current.getBoundingClientRect().height;
    if (menuTopValue + menuHeightValue >= containerButtomValue) {
      listWrapperClassnameRef.current = 'autocomplete__dropdown--bottom';
    }
  }

  useEffect(() => {
    onInputChange(pendingInput);
    isTextMatched.current =
      editMode && checkIfTextMatchSelectOptions(pendingInput);
    // To simulate dropdown select default behaviour
    // Clear the text input if a match is found and currentItemLabelLength match textinput length
    // or text input exceeds items with longest string length
    const currentItemLabelLength =
      currentActiveIndex.current != null
        ? options[currentActiveIndex.current].label.length
        : 0;
    if (
      useDefaultSelect &&
      (isTextMatchFound(currentItemLabelLength) ||
        doesTextInputExceedsItemsLongestString())
    ) {
      clearTextInput();
    }
  }, [pendingInput]);

  const handleOnSelect = (selection) => {
    if (selection && selection.disabled) return;
    onChange(selection || value);
    clearTextInput();
  };

  const handleInputChange = (inputValue) => {
    setEditMode(true);
    optionAriaLabel !== '' && setOptionAriaLabel('');
    setPendingInput(inputValue);
  };

  const handleOnBlur = (closeMenu) => (event) => {
    event.nativeEvent.preventDownshiftDefault = true;
    setOptionAriaLabel('');
    clearTextInput();
    closeMenu();
  };

  const handleOnFocus = (openMenu) => (event) => {
    event.nativeEvent.preventDownshiftDefault = true;
    setEditMode(true);
    clearTextInput();
    openMenu();
  };

  const onStateChange = (values, state) => {
    stateChangeCallback && stateChangeCallback(state);
    currentActiveIndex.current = state.highlightedIndex;
    if (state.isOpen) {
      update();
    } else {
      setEditMode(false);
    }
  };

  const handleStatusMessage = ({ highlightedItem }) => {
    if (highlightedItem && highlightedItem.label) {
      setOptionAriaLabel(highlightedItem.label);
    }
  };

  // It checks if the input text matches one dropdown items
  // If true, auto highlight the matched item and scroll the item Into View
  const checkIfTextMatchSelectOptions = (pendingValue) => {
    const inputValue = deburr(pendingValue.trim()).toLowerCase();
    if (inputValue.length === 0) {
      return false;
    }
    // Check if inputValue value matches options items and filter the data.
    const filteredOptions = options.filter((option) =>
      option.label.startsWith(inputValue),
    );
    // Check if the get the index of the first matched options.
    const filteredItemIndex =
      filteredOptions.length > 0
        ? options.findIndex(
            (option) =>
              option.label.toLowerCase() ===
              filteredOptions[0].label.toLowerCase(),
          )
        : -1;
    if (editMode && filteredItemIndex > -1 && listItemsRef.current.length > 0) {
      currentActiveIndex.current = filteredItemIndex;
      const element = listItemsRef.current[filteredItemIndex];
      element && element.scrollIntoView();
      element && element.classList.add('is_active');
    }
    return filteredItemIndex !== -1;
  };

  const handleKeyDown = () => {
    // This helps to click the currently highlighted item on enter key press.
    if (currentActiveIndex.current !== null) {
      const element = listItemsRef.current[currentActiveIndex.current];
      element && element.click();
      currentActiveIndex.current = null;
      setEditMode(false);
    }
  };

  const clearTextInput = () => setPendingInput('');

  const isTextMatchFound = (currentItemLabelLength) =>
    isTextMatched.current && pendingInput.length >= currentItemLabelLength;

  const doesTextInputExceedsItemsLongestString = () =>
    pendingInput.length > itemStringMaxLength;

  const { label: valueLabel = '', value: selectedValue } = value || {};

  const containerClassName =
    'absolute overflow-auto w-full border border-solid border-gray-400 rounded-0.8 bg-white';

  const _listClassName = cn(
    'autocomplete__dropdown',
    'max-h-96 shadow-sm mt-0.8 z-10 p-4.2',
    'flex flex-column items-start w-max',
    listWrapperClassnameRef.current,
    listClassName,
  );

  return (
    <Downshift
      ref={downshiftRef}
      itemToString={(item) => (item ? item.label : '')}
      initialHighlightedIndex={0}
      initialInputValue={valueLabel}
      onSelect={handleOnSelect}
      onStateChange={onStateChange}
      onInputValueChange={handleInputChange}
      getA11yStatusMessage={handleStatusMessage}
      {...downshiftProps}
    >
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        isOpen,
        highlightedIndex,
        openMenu,
        closeMenu,
      }) => {
        const inputValue =
          !disableDropdown && (editMode || useDefaultSelect)
            ? pendingInput
            : valueLabel;
        const placeholderValue =
          (placeholder && placeholder.label) || valueLabel;

        // https://github.com/downshift-js/downshift/issues/1272#issuecomment-1063244446
        const menuProps = getMenuProps(
          { ref: menuRef },
          { suppressRefError: true },
        );
        const menuId = menuProps.id;
        const _inputProps = getInputProps({
          'data-test-id': 'autocompleteselect-input-container',
          placeholder: placeholderValue,
          value: inputValue,
          onClick: openOnClick ? (isOpen ? closeMenu : openMenu) : null,
          onFocus:
            disableDropdown || disableAutoFocus
              ? null
              : handleOnFocus(openMenu),
          onBlur: disableDropdown ? null : handleOnBlur(closeMenu),
          // Add accessibility tags for combobox
          // https://academicmerit.atlassian.net/browse/FY-31199
          role: 'combobox',
          'aria-live': 'polite',
          'aria-label': props['aria-label']
            ? props['aria-label']
            : `Selected option: ${valueLabel}`,
          'aria-expanded': editMode,
          'aria-controls': menuId,
          'aria-labelledby': null,
          ...inputProps,
        });

        return (
          <div
            className={cn('autocomplete rounded-24.8 relative', className)}
            onKeyDown={(event) => {
              if (event.keyCode === 13) {
                event.preventDefault();
                handleKeyDown();
                inputRef && inputRef.current && inputRef.current.focus();
              }
            }}
            {...props}
            // The reason of these prop being here is that somehow they're still set even if not present
            // Most probabily by Downshift component
            aria-labelledby={null}
            role="presentation"
          >
            <div ref={setReferenceElement}>
              <Input
                disableInput={disableDropdown}
                icon={icon}
                iconOnClick={
                  clearable ? customReset : isOpen ? closeMenu : openMenu
                }
                inputRef={inputRef}
                inputClassName={inputClassName}
                inputElemProps={_inputProps}
                trackingId={trackingId}
              />
            </div>
            {createPortal(
              <div
                className={portalContainerClassName}
                ref={setPopperElement}
                {...attributes.popper}
                style={styles.popper}
              >
                {isOpen && !disableDropdown ? (
                  <div className={containerClassName}>
                    {editMode && !useDefaultSelect ? (
                      <ul
                        {...menuProps}
                        className={_listClassName}
                        aria-labelledby={_inputProps.id}
                      >
                        {renderSuggestions(pendingInput, valueLabel, options, {
                          showEmpty,
                          getItemProps,
                          highlightedIndex,
                        })}
                      </ul>
                    ) : (
                      <ul
                        {...menuProps}
                        ref={menuRef}
                        className={_listClassName}
                        aria-labelledby={_inputProps.id}
                      >
                        {options.map((item, index) => (
                          <ListItem
                            key={`${item.label}-${item.value}`}
                            item={item}
                            matchingValue={selectedValue === item.value}
                            highlightedIndex={highlightedIndex}
                            index={index}
                            getItemProps={getItemProps}
                            ref={(element) =>
                              (listItemsRef.current[index] = element)
                            }
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>,
              document.querySelector('#autocomplete'),
            )}
          </div>
        );
      }}
    </Downshift>
  );
};

AutocompleteSelect.propTypes = {
  onInputChange: PropTypes.func,
  inputClassName: PropTypes.string,
};

AutocompleteSelect.defaultProps = {
  placeholder: null,
  onInputChange: () => {},
  inputClassName: '',
  trackingId: null,
};

export default AutocompleteSelect;
